import type { ProjectBrief, Task } from '@dyspatch/core';
import { PencilLine } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Surface } from '@/components/ui/section';
import { ActionRow, ActionsPopover } from '@/features/actions/ActionsPopover';
import {
  CancelBriefActions,
  PlanActions,
  RequestPlanActions,
} from '@/features/actions/GateActions';
import { AddedTasksActions, PauseBriefActions } from '@/features/actions/PauseActions';
import { hasOpenGate, isAmendable, isCancellable, isPausable } from '@/lib/briefs';
import { openQuestionsLine } from '@/lib/questions';

/**
 * The decision regions of a job page.
 *
 * Who opened the job, its state and who approved it (and when) used to live in
 * their own band here, drawn once beneath the header (`JobDecisionFacts`,
 * issue #64). Those are facts about the record rather than a decision, so they
 * moved into the job's property grid (`JobPropertyGrid` in `BriefDetail.tsx`)
 * alongside the rest of its scalars, and this file keeps only the two regions
 * that are actually decisions.
 *
 * `JobDecision` is the gate itself: the one move the job is actually waiting for,
 * which is why the page keeps it pinned under the header while you read the plan
 * it is a decision about. `JobVerbs` is the standing set - amend, pause, cancel -
 * which is always available on a job past Go but is not what anybody is waiting
 * on, so it sits below rather than in the pinned strip.
 */
function GateCopy({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="px-gutter pt-3">
      <div className="dy-eyebrow text-gate">{label}</div>
      <p className="text-fg-2 mt-1 max-w-[88ch] text-sm">{children}</p>
    </div>
  );
}

export function JobDecision({
  brief,
  tasks,
  added,
  needsPlan,
  openQuestions = 0,
}: {
  brief: ProjectBrief;
  /** Every step of the job, for the count Go queues. */
  tasks: readonly Task[];
  /** The steps an amendment added that nobody has approved yet. */
  added: readonly Task[];
  needsPlan: boolean;
  /**
   * How many of the planner's questions nobody has answered
   * (docs/PLAN-QUESTIONS.md). It is said here and nowhere louder: the gate is
   * Go, not the questions, and each one is already planned on an assumption.
   * The line exists so nobody presses Go without knowing they are deciding
   * something, not to hold the button.
   */
  openQuestions?: number;
}) {
  if (!hasOpenGate(brief, tasks)) return null;

  return (
    <Surface tone="gate" raised>
      {needsPlan ? (
        <>
          <GateCopy label="Your gate · needs a plan">
            Nothing is happening on this job yet. Requesting a plan queues one planning task on{' '}
            {brief.repos[0]}; a worker reads the code and drafts the graph, then it comes back here
            for your approval.
          </GateCopy>
          <RequestPlanActions briefId={brief.id} />
        </>
      ) : null}

      {brief.status === 'awaiting_approval' ? (
        <>
          <GateCopy label="Your gate · plan approval">
            Go queues {tasks.length} task{tasks.length === 1 ? '' : 's'} to the fleet. Nothing runs
            until you press it. Notes left on a task fold into that task's spec at Go, so what you
            approve is what the agent is given.
          </GateCopy>
          {openQuestions ? (
            <p className="text-fg-2 px-gutter pt-1.5 max-w-[88ch] text-sm">
              {openQuestionsLine(openQuestions)}
            </p>
          ) : null}
          <PlanActions briefId={brief.id} />
        </>
      ) : null}

      {added.length ? (
        <>
          <GateCopy label="Your gate · added tasks">
            An amendment added {added.length} task{added.length === 1 ? '' : 's'} (
            {added.map((task) => task.key).join(', ')}). They wait here until you approve them; the
            rest of the job carries on.
          </GateCopy>
          <AddedTasksActions briefId={brief.id} count={added.length} />
        </>
      ) : null}
    </Surface>
  );
}

/**
 * The job's standing verbs, one control in the header (issue #89).
 *
 * Amend, Pause and Cancel used to be a full-width card of three bordered
 * bands, immediately under the header and above the goal and the plan - about
 * forty words and a hundred fifty vertical pixels spent on the three things a
 * person does least often on this page, in the band where the eye lands
 * first. Now it is one control beside the title, and a popover holding the
 * same three verbs with the same sentences, so the goal and the plan start
 * where the bands used to be.
 *
 * Amend hands off to a section of the page rather than confirming in place -
 * it opens `AmendComposer` below the gate - so its button closes the popover
 * on click; Pause and Cancel confirm inside the popover itself, the same
 * `AskBox` they always used, just floating instead of pinned to the page.
 */
export function JobVerbs({
  brief,
  pausableCount,
  pausedCount,
  mayShape,
  amending,
  onAmend,
}: {
  brief: ProjectBrief;
  /** How many steps a press of Pause would actually hold: unfinished, not already held. */
  pausableCount: number;
  /** How many steps carry the pause flag, so Resume can say how many it frees. */
  pausedCount: number;
  /** The person who filed the job, or an admin (docs/JOB-LIFECYCLE.md decision 4). */
  mayShape: boolean;
  amending: boolean;
  onAmend: () => void;
}) {
  const amendable = mayShape && isAmendable(brief);
  const pausable = mayShape && isPausable(brief);
  const cancellable = isCancellable(brief);
  if (!amendable && !pausable && !cancellable) return null;

  return (
    <ActionsPopover label="Job actions">
      {(close) => (
        <>
          {amendable ? (
            <ActionRow>
              <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 px-gutter py-2.5">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onAmend();
                    close();
                  }}
                  aria-expanded={amending}
                  className="max-sm:h-9 max-sm:px-3"
                >
                  <PencilLine aria-hidden />
                  {amending ? 'Close the amendment' : 'Amend'}
                </Button>
                <span className="text-fg-3 text-xs">
                  {brief.status === 'done'
                    ? 'Change the goal or add tasks; the finished job reopens.'
                    : 'Change the goal, add rules or add tasks without stopping what runs.'}
                </span>
              </div>
            </ActionRow>
          ) : null}

          {/* A job stays `approved` until its last step ends, so the brief's
              status cannot say whether there is anything left to hold: a job
              whose every step has failed is still approved, and the row
              drawn over it could only come back as a 409. The counts decide
              it instead. */}
          {pausable && (pausableCount || pausedCount) ? (
            <ActionRow
              sentence={
                pausedCount
                  ? `${pausedCount} task${pausedCount === 1 ? ' is' : 's are'} paused. Resume continues each from its branch.${
                      pausableCount
                        ? ` ${pausableCount} more ${pausableCount === 1 ? 'is' : 'are'} still live.`
                        : ''
                    }`
                  : 'Pausing keeps the work: a running task pushes what it has and stops, and Resume continues it.'
              }
            >
              <PauseBriefActions
                briefId={brief.id}
                pausableCount={pausableCount}
                pausedCount={pausedCount}
              />
            </ActionRow>
          ) : null}

          {cancellable ? (
            <ActionRow sentence="Dispatched. Cancelling stops every unfinished task and closes the job. A task already running on a laptop is stopped within a heartbeat.">
              <CancelBriefActions briefId={brief.id} />
            </ActionRow>
          ) : null}
        </>
      )}
    </ActionsPopover>
  );
}
